/**
 * Stale-deploy recovery for lazy route chunks.
 *
 * After a deploy, a tab still running the previous build asks for hashed
 * chunk files that no longer exist. Netlify's SPA fallback serves index.html
 * in their place, so the browser rejects the `import()` with a TypeError
 * ("Failed to fetch dynamically imported module" / "Importing a module script
 * failed" / "error loading dynamically imported module"). That is not an API
 * outage — the cure is a single full reload to pick up the new chunk map.
 */

const RELOAD_FLAG = 'realm:stale-chunk-reload'

/** True when the error is a failed dynamic `import()` of a route chunk. */
export function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  return /dynamically imported module|Importing a module script failed|Failed to fetch dynamically|ChunkLoadError|Loading chunk/i.test(
    err.message,
  )
}

/**
 * Reload the page once for the current URL and return true; return false (no
 * reload) if this URL already reloaded for a stale chunk, so a persistent
 * failure surfaces as an error instead of a reload loop.
 */
export function reloadOnceForStaleChunk(): boolean {
  if (typeof window === 'undefined') return false
  const key = `${RELOAD_FLAG}:${window.location.pathname}`
  try {
    if (sessionStorage.getItem(key)) {
      sessionStorage.removeItem(key)
      return false
    }
    sessionStorage.setItem(key, '1')
  } catch {
    return false
  }
  window.location.reload()
  return true
}
